/**
 * Create / edit a PowerPanel — top of the power-topology hierarchy.
 * Mounted at:
 *   /facilities/electrical/panels/new
 *   /facilities/electrical/panels/:id/edit
 */
import {
  Alert,
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import WorkspacePage from '../components/landing/WorkspacePage';
import {
  electricalTopologyAPI,
  inventoryAPI,
  PowerCircuitDetail,
  PowerPanelPhase,
  PowerPanelWritable,
} from '../services/api';
import { Location } from '../types';
import { extractErrorMessage } from '../utils/extractErrorMessage';

const PHASE_OPTIONS: Array<{ value: PowerPanelPhase; label: string }> = [
  { value: 'single', label: 'Single phase' },
  { value: 'split', label: 'Split phase 120/240V' },
  { value: 'three', label: 'Three phase' },
];

const BREAKER_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Not specified' },
  { value: 'SQUARE_D_QO', label: 'Square D QO (plug-on, 10kA)' },
  { value: 'SQUARE_D_HOMELINE', label: 'Square D Homeline (plug-on, 10kA)' },
  { value: 'EATON_CH', label: 'Eaton CH / Cutler-Hammer Classic' },
  { value: 'EATON_BR', label: 'Eaton BR (residential)' },
  { value: 'SIEMENS_QP', label: 'Siemens QP / Murray MP' },
  { value: 'GE_Q_LINE', label: 'GE Q-Line / ABB Q-Line' },
  { value: 'FEDERAL_PACIFIC', label: 'Federal Pacific Stab-Lok (legacy — replace)' },
  { value: 'PUSHMATIC', label: 'Pushmatic / ITE-Bulldog (legacy)' },
  { value: 'DIN_RAIL', label: 'IEC DIN-rail (C60 / industrial)' },
  { value: 'OTHER', label: 'Other / unknown' },
];

const NUMBERING_OPTIONS: Array<{ value: 'top_down' | 'bottom_up'; label: string }> = [
  { value: 'top_down', label: 'Top-down (slot 1 at top)' },
  { value: 'bottom_up', label: 'Bottom-up (slot 1 at bottom)' },
];

const PowerPanelFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [locations, setLocations] = useState<Location[]>([]);
  const [circuits, setCircuits] = useState<PowerCircuitDetail[]>([]);
  const [form, setForm] = useState<Partial<PowerPanelWritable>>({
    name: '',
    phase_configuration: 'split',
    voltage: 240,
    main_breaker_amperage: null,
    breaker_type: '',
    numbering_direction: 'top_down',
    manufacturer: '',
    model: '',
    install_date: null,
    notes: '',
    needs_review: false,
    fed_by: null,
  });
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inventoryAPI
      .listLocations()
      .then((response) => setLocations(response.data.results))
      .catch((err) => console.warn('Failed to load locations', err));
    // All circuits are candidate feeders for any sub-panel. We filter out
    // the current panel's own circuits below so the operator can't
    // self-feed via the dropdown.
    electricalTopologyAPI
      .listCircuits()
      .then((response) => setCircuits(response.data.results))
      .catch((err) => console.warn('Failed to load circuits', err));
  }, []);

  const loadPanel = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await electricalTopologyAPI.getPanel(id);
      const panel = response.data;
      setForm({
        location: panel.location,
        name: panel.name,
        phase_configuration: panel.phase_configuration,
        voltage: panel.voltage,
        main_breaker_amperage: panel.main_breaker_amperage,
        breaker_type: panel.breaker_type,
        numbering_direction: panel.numbering_direction,
        manufacturer: panel.manufacturer,
        model: panel.model,
        install_date: panel.install_date,
        notes: panel.notes,
        needs_review: panel.needs_review,
        fed_by: panel.fed_by,
      });
      setError(null);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load panel.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) loadPanel();
  }, [isEditMode, loadPanel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.location) {
      setError('Name and location are required.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      if (isEditMode && id) {
        await electricalTopologyAPI.updatePanel(id, form);
        navigate(`/facilities/electrical/panels/${id}`);
      } else {
        const response = await electricalTopologyAPI.createPanel(form);
        navigate(`/facilities/electrical/panels/${response.data.id}`);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to save panel.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspacePage
      testId="power-panel-form-page"
      hero={{
        eyebrow: 'Electrical · Topology',
        title: isEditMode ? 'Edit power panel' : 'New power panel',
        description: 'Load centers anchor the topology — everything else hangs off them.',
        action: (
          <Button variant="default" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        ),
      }}
    >
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper withBorder p="md">
          <Text c="dimmed">Loading panel…</Text>
        </Paper>
      ) : (
        <Paper withBorder p="md">
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Name"
                required
                value={form.name ?? ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Sewing Panel A"
              />
              <Select
                label="Location"
                required
                data={locations.map((loc) => ({ value: String(loc.id), label: loc.name }))}
                value={form.location != null ? String(form.location) : null}
                onChange={(value) =>
                  setForm({ ...form, location: value ? Number(value) : undefined })
                }
                searchable
                placeholder="Where the panel is mounted"
              />
              <Select
                label="Phase configuration"
                data={PHASE_OPTIONS}
                value={form.phase_configuration ?? 'split'}
                onChange={(value) =>
                  setForm({ ...form, phase_configuration: (value as PowerPanelPhase) ?? 'split' })
                }
              />
              <Group grow>
                <NumberInput
                  label="Voltage"
                  value={form.voltage ?? 240}
                  onChange={(value) =>
                    setForm({ ...form, voltage: typeof value === 'number' ? value : 240 })
                  }
                  min={1}
                  suffix=" V"
                />
                <NumberInput
                  label="Main breaker amperage"
                  description="Leave blank if no main / sub-fed"
                  value={form.main_breaker_amperage ?? ''}
                  onChange={(value) =>
                    setForm({
                      ...form,
                      main_breaker_amperage: typeof value === 'number' ? value : null,
                    })
                  }
                  min={1}
                  suffix=" A"
                />
              </Group>
              <Group grow>
                <Select
                  label="Breaker family"
                  description="Used to filter replacement sourcing and flag mismatched breakers."
                  data={BREAKER_TYPE_OPTIONS}
                  value={form.breaker_type ?? ''}
                  onChange={(value) =>
                    setForm({ ...form, breaker_type: (value as any) ?? '' })
                  }
                />
                <Select
                  label="Numbering direction"
                  description="Which physical slot is breaker 1? Most North American panels are top-down."
                  data={NUMBERING_OPTIONS}
                  value={form.numbering_direction ?? 'top_down'}
                  onChange={(value) =>
                    setForm({
                      ...form,
                      numbering_direction: (value as 'top_down' | 'bottom_up') ?? 'top_down',
                    })
                  }
                />
              </Group>
              <Group grow>
                <TextInput
                  label="Manufacturer"
                  value={form.manufacturer ?? ''}
                  onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                />
                <TextInput
                  label="Model"
                  value={form.model ?? ''}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                />
              </Group>
              <TextInput
                label="Install date"
                type="date"
                value={form.install_date ?? ''}
                onChange={(e) => setForm({ ...form, install_date: e.target.value || null })}
              />
              <Select
                label="Fed by (upstream feeder circuit)"
                description="Leave blank for the main / service-entrance panel. Pick the parent panel's feeder circuit for a sub-panel."
                placeholder="No upstream feeder (main panel)"
                clearable
                searchable
                data={circuits
                  .filter((c) => !id || c.panel_id !== Number(id))
                  .map((c) => ({
                    value: String(c.id),
                    label: `${c.panel_name} · breaker ${c.breaker_label || c.breaker} · ${c.label || `Circuit ${c.id}`}`,
                  }))}
                value={form.fed_by != null ? String(form.fed_by) : null}
                onChange={(value) =>
                  setForm({ ...form, fed_by: value ? Number(value) : null })
                }
              />
              <Textarea
                label="Notes"
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                autosize
                minRows={2}
              />
              <Group justify="flex-end">
                <Button type="button" variant="default" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button type="submit" loading={saving}>
                  {isEditMode ? 'Save changes' : 'Create panel'}
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      )}
    </WorkspacePage>
  );
};

export default PowerPanelFormPage;
